const SESSION_URL = '/apps/inventory/cadenas/session';
const MIDENT_LOOKUP_URL = '/apps/inventory/cadenas/mident';

function isValidMident(value) {
  return value.includes('VTYP=3D') && value.includes('ORDERNO=') && !value.includes('...');
}

function loadStylesheet(href) {
  const existing = document.querySelector(`link[data-cadenas-css="${href}"]`);
  if (existing) {
    return existing.sheet
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener('error', () => reject(new Error('Could not load Cadenas CSS')), { once: true });
        });
  }

  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.cadenasCss = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error('Could not load Cadenas CSS'));
    document.head.appendChild(link);
  });
}

async function resolveMident(raw) {
  if (isValidMident(raw)) return raw;

  const params = new URLSearchParams({ q: raw });
  const res = await fetch(`${MIDENT_LOOKUP_URL}?${params}`, {
    credentials: 'same-origin',
  });

  if (!res.ok) {
    throw new Error(`Could not resolve cadenas_mident (${res.status}). Fix the metafield or run sync-midents.`);
  }

  const data = await res.json();
  if (!data.mident || !isValidMident(data.mident)) {
    throw new Error('cadenas_mident could not be matched in allMidents.csv for this product.');
  }

  return data.mident;
}

async function fetchSession() {
  const res = await fetch(SESSION_URL, { credentials: 'same-origin' });
  if (!res.ok) {
    if (res.status === 429) {
      throw new Error('3D viewer blocked by Vercel (429). Check App Proxy bypass or disable Firewall mitigations.');
    }
    throw new Error(`Could not load 3D viewer (${res.status}).`);
  }
  return res.json();
}

function waitForLayout() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function showError(root, message) {
  root.classList.remove('is-ready');
  const el = root.querySelector('[data-cadenas-error]');
  if (el) {
    el.textContent = message;
    el.hidden = false;
  }
}

function isCadenasBrandingElement(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (el.querySelector('canvas')) return false;

  const text = el.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  if (!text || text.length > 120) return false;

  return /powered by|cadenas|©\s*\d{4}/i.test(text);
}

function hideCadenasBranding(viewerContainer) {
  const hideMatches = () => {
    viewerContainer.querySelectorAll('a, span, p, div, footer, label').forEach((el) => {
      if (!isCadenasBrandingElement(el)) return;

      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
      el.setAttribute('aria-hidden', 'true');
    });
  };

  hideMatches();

  const observer = new MutationObserver(hideMatches);
  observer.observe(viewerContainer, { childList: true, subtree: true, characterData: true });

  window.setTimeout(() => observer.disconnect(), 60000);
}

export async function initCadenasViewer(root) {
  if (!root || root.dataset.cadenasStarted === 'true') return;
  root.dataset.cadenasStarted = 'true';

  const rawMident = root.dataset.mident;
  if (!rawMident) return;

  const viewerContainer = root.querySelector('[data-cadenas-webviewer]');
  if (!viewerContainer?.id) return;

  try {
    const mident = await resolveMident(rawMident);
    const session = await fetchSession();

    await loadStylesheet(session.cssUrl ?? session.cdnUrl.replace(/index\.js$/, 'webcomponents.css'));
    await waitForLayout();

    const CNS = await import(session.cdnUrl);

    CNS.Core.setServiceBaseUrl(session.serviceBaseUrl);
    CNS.Core.setApiKey(session.apiKey);
    CNS.Core.setUserInfo(session.userInfo);

    CNS.showWebViewer3d(
      {
        masterObject: { mident },
        syncContextID: root.dataset.syncContextId || '1',
      },
      viewerContainer.id
    );

    hideCadenasBranding(viewerContainer);

    root.classList.add('is-ready');

    window.setTimeout(() => {
      const canvas = viewerContainer.querySelector('canvas');
      if (!canvas) {
        showError(root, '3D model did not render. Check browser console.');
      }
    }, 15000);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('Cadenas viewer:', message);
    showError(root, message);
  }
}

window.initCadenasViewer = initCadenasViewer;
